import { CitationData } from '@chegg-wt/core/types/form'
import { Contributor } from '@chegg-wt/core/types/redux-citation-api'
import {
  CreateCitationMetadata,
  CitationDataForRio,
  AutociteResponseForRioAuthors,
  AutociteResponseForRioEditors,
  AutociteResponseForRioTranslators,
  UpdateCitationMetadata,
} from '@chegg-wt/core/types/autocite'

import jsCookie from 'js-cookie'
import {
  AutociteMetadataInput,
  AutociteAuthStateEnum,
} from '@chegg-wt/core/globalTypesOneGraph'
import { months, formattedAutociteSource } from './searchSources'

export const adobeCookieName = 'AMCV_3FE7CBC1556605A77F000101@AdobeOrg'
const sessionCookieName = 'CSID'
const visitorCookieName = 'CVID'

export const mapAutociteContributors = (
  contributors: any[],
  contributorFunction: string
): any[] => {
  if (!Array.isArray(contributors)) return []
  return contributors.map((contrib) => ({
    [`${contributorFunction}GivenName`]: contrib.given,
    [`${contributorFunction}LastName`]: contrib.family,
  }))
}

export const cslToRioAutociteResponse = (result: any): AutociteMetadataInput => {
  const { csl, metrics } = result
  const dateAccessed = csl.accessed['date-parts'][0]
  const dateIssued = csl.issued['date-parts'][0]
  const resp = {
    accessedPublicationDay: parseInt(dateAccessed[2], 10),
    accessedPublicationMonth: dateAccessed[1] ? months[dateAccessed[1] - 1] : '',
    accessedPublicationYear: parseInt(dateAccessed[0], 10),
    authors: mapAutociteContributors(csl.author, 'author'),
    containerTitle: csl['container-title'],
    contentSource: formattedAutociteSource(csl.type),
    contentTitle: csl.title,
    editors: mapAutociteContributors(csl.editor, 'editor'),
    issuedPublicationDay: parseInt(dateIssued[2], 10),
    issuedPublicationMonth: dateIssued[1] ? months[dateIssued[1] - 1] : '',
    issuedPublicationYear: parseInt(dateIssued[0], 10),
    metricsRule: metrics?.rule,
    metricsRuleVersion: metrics?.ruleVersion,
    publisherName: csl.publisher,
    translators: mapAutociteContributors(csl.translator, 'translator'),
    url: csl.URL,
  }
  return resp
}

export const numberOfAutociteFields = (csl: any): number => {
  let count = 0
  count += Number(
    !!(
      csl.accessed['date-parts'][0] &&
      (csl.accessed['date-parts'][0][0] ||
        csl.accessed['date-parts'][0][1] ||
        csl.accessed['date-parts'][0][2])
    )
  )
  count += Number(
    !!(
      csl.issued['date-parts'][0] &&
      (csl.issued['date-parts'][0][0] ||
        csl.issued['date-parts'][0][1] ||
        csl.issued['date-parts'][0][2])
    )
  )
  count += Number(Array.isArray(csl.author) && csl.author.length > 0)
  count += Number(Array.isArray(csl.editor) && csl.editor.length > 0)
  count += Number(Array.isArray(csl.translator) && csl.translator.length > 0)
  count += Number(!!csl['container-title'])
  count += Number(!!csl.type)
  count += Number(!!csl.title)
  count += Number(!!csl.publisher)
  count += Number(!!csl.URL)
  return count
}

export const mapCitationContributors = (contributors: Contributor[]) => {
  const authorsArray = [] as AutociteResponseForRioAuthors[]
  const editorsArray = [] as AutociteResponseForRioEditors[]
  const translatorsArray = [] as AutociteResponseForRioTranslators[]
  if (
    contributors.length === 1 &&
    contributors[0].first === '' &&
    contributors[0].last === ''
  ) {
    return { authorsArray, editorsArray, translatorsArray }
  }
  contributors.forEach((contrib) => {
    if (contrib.function === 'author') {
      authorsArray.push({
        author_given_name: contrib.first,
        author_family_name: contrib.last,
      })
    } else if (contrib.function === 'editor') {
      editorsArray.push({
        editor_given_name: contrib.first,
        editor_family_name: contrib.last,
      })
    } else {
      translatorsArray.push({
        translator_given_name: contrib.first,
        translator_family_name: contrib.last,
      })
    }
  })
  return { authorsArray, editorsArray, translatorsArray }
}

export const mapCitationDataToRio = (citationData: any): CitationDataForRio => {
  const { authorsArray, editorsArray, translatorsArray } = mapCitationContributors(
    citationData.contributors
  )
  const resp = {
    type: formattedAutociteSource(citationData.source),
    title: citationData[formattedAutociteSource(citationData.source)]?.title,
    publisher_name: citationData.pubonline?.inst,
    author_name: authorsArray,
    editor_name: editorsArray,
    translator_name: translatorsArray,
    issued_publication_year: parseInt(citationData.pubonline?.year, 10),
    issued_publication_month: citationData.pubonline?.month,
    issued_publication_day: parseInt(citationData.pubonline?.day, 10),
    accessed_publication_year: parseInt(citationData.pubonline?.yearaccessed, 10),
    accessed_publication_month: citationData.pubonline?.monthaccessed,
    accessed_publication_day: parseInt(citationData.pubonline?.dayaccessed, 10),
    container_title: citationData.pubonline?.title,
    url: citationData.pubonline?.url,
  }
  return resp
}

export const getCreateCitationMetadata = (
  citationData: CitationData,
  cslData: AutociteMetadataInput,
  trackingId: string,
  citationId: string
): CreateCitationMetadata => {
  const mappedCitationData = mapCitationDataToRio(citationData)
  const updatedMappedCitationData = { ...mappedCitationData }
  updatedMappedCitationData.metrics_rule = cslData.metricsRule
  updatedMappedCitationData.metrics_ruleVersion = cslData.metricsRuleVersion

  Object.keys(updatedMappedCitationData).forEach((key) => {
    if (!updatedMappedCitationData[key]) delete updatedMappedCitationData[key]
  })

  const citationMetadata = {} as any
  citationMetadata.tracking_id = trackingId
  citationMetadata.citation_id = citationId
  citationMetadata.citation_fill_data = updatedMappedCitationData

  return citationMetadata
}

export const getUpdateCitationMetadata = (
  citationData: CitationData,
  citationId: string
): UpdateCitationMetadata => {
  const mappedCitationData = mapCitationDataToRio(citationData)
  const updatedMappedCitationData = { ...mappedCitationData }

  Object.keys(updatedMappedCitationData).forEach((key) => {
    if (!updatedMappedCitationData[key]) delete updatedMappedCitationData[key]
  })

  const citationMetadata = {} as any
  citationMetadata.citation_id = citationId
  citationMetadata.citation_fill_data = updatedMappedCitationData
  return citationMetadata
}

export const getAdobeMcid = () => {
  if (!window || window.location.href.includes('rsw_disable_adobe_cookie')) {
    return undefined
  }
  try {
    const adobeCookie = jsCookie.get(adobeCookieName)
    const decodedCookie = decodeURI(adobeCookie)
    const segments = decodedCookie.split('|')
    const mcidMarkerIndex = segments.indexOf('MCMID')
    if (mcidMarkerIndex !== -1) {
      return segments[mcidMarkerIndex + 1]
    }
    return null
  } catch (error) {
    return null
  }
}
export const getAuthState = (
  loggedIn: boolean,
  softLoggedIn: boolean
): AutociteAuthStateEnum => {
  if (loggedIn) {
    if (softLoggedIn) return AutociteAuthStateEnum.SOFT_LOGGED_IN
    return AutociteAuthStateEnum.HARD_LOGGED_IN
  }
  return AutociteAuthStateEnum.LOGGED_OUT
}

export const getClientCommonFields = (
  softLoggedIn: boolean,
  cheggUserUuid?: string | null
) => {
  return {
    adobeMcid: getAdobeMcid(),
    authState: getAuthState(!!cheggUserUuid, softLoggedIn),
    cheggSessionId: parseInt(jsCookie.get(sessionCookieName), 10),
    cheggVisitorId: jsCookie.get(visitorCookieName),
    referralUrl: window && window.document.referrer,
    userUuid: cheggUserUuid,
  }
}
